// Admin API client — /admin/autobuild/* (mirrors the API's autobuild aggregate).

import type { AutobuildSettings, OrchestratorAccount } from '@a7y/types';
import type {
  AutobuildOverview,
  AutobuildHistory,
  AutobuildKind,
  AutobuildSessionDetail,
  AiQuotaStatus,
  AiQuotaRefreshResponse,
  AutomationLeaseStatus,
  CdidQueue,
  CdidFlowHistory,
  CdidCyclesResponse,
  ApdDeployQueue,
  AdgsQueue,
  AdgsSweepWindow,
  AddisEnablement,
  AddisPauseResponse,
  AddisStatus,
  WorkloadsStatus,
  UkkoSlotsStatus,
} from '@a7y/types';
import { client } from './transport';

export const autobuildApi = {
  // Autobuild Console (CDID / AHC / APD — BQ auto_session_log)
  getAutobuildOverview: () => client.get<AutobuildOverview>('/autobuild/overview'),
  getAutobuildHistory: (kind: AutobuildKind, limit = 30) =>
    client.get<AutobuildHistory>(`/autobuild/history/${kind}?limit=${limit}`),
  getAutobuildSession: (kind: AutobuildKind, stamp: string) =>
    client.get<AutobuildSessionDetail>(`/autobuild/session/${kind}/${encodeURIComponent(stamp)}`),
  getAiQuotaStatus: () => client.get<AiQuotaStatus>('/autobuild/quota'),
  refreshAiQuotaStatus: () => client.post<AiQuotaRefreshResponse>('/autobuild/quota/refresh', {}),
  /** A7Y-2821 driver leases, reservations, handoffs and circuit state. */
  getAutomationLeaseStatus: () => client.get<AutomationLeaseStatus>('/autobuild/leases'),
  getCdidQueue: () => client.get<CdidQueue>('/autobuild/cdid/queue'),
  /** Trailing-window arrivals-vs-drain flow balance, for "is CDID keeping up
   *  with its own queue?" — see `CdidFlowHistory`. */
  getCdidFlowHistory: () => client.get<CdidFlowHistory>('/autobuild/cdid/flow'),
  /** Cycle-shaped Activity data for the CDID tab — health strip plus one row
   *  per material cycle, replacing the per-session RunHistory table there. */
  getCdidCycles: () => client.get<CdidCyclesResponse>('/autobuild/cdid/cycles'),
  getAutobuildSettings: () => client.get<AutobuildSettings>('/autobuild/settings'),
  setOrchestratorAccount: (orchestratorAccount: OrchestratorAccount) =>
    client.patch<AutobuildSettings>('/autobuild/settings/orchestrator', {
      orchestratorAccount,
    }),
  getAdgsQueue: () => client.get<AdgsQueue>('/autobuild/adgs/queue'),
  /** Sweeps for one window. The CALLER computes the bounds — the server cannot know the viewer's zone. */
  getAdgsSweeps: (from: string, to: string) =>
    client.get<AdgsSweepWindow>(
      `/autobuild/adgs/sweeps?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
    ),
  getApdDeployQueue: () => client.get<ApdDeployQueue>('/autobuild/apd/queue'),
  /** The `/a7y-addis-status` board: four systems, their lights, and the prod chain. */
  getAddisStatus: () => client.get<AddisStatus>('/autobuild/addis-status'),
  /** `/admin/automation/overview` — is each ADDIS system currently on, and what is holding it off. */
  getAddisEnablement: () => client.get<AddisEnablement>('/autobuild/addis-enablement'),
  /**
   * Pause or resume one system — the console's `scripts/<system>-pause.cjs`.
   * The response carries the value read BACK from GitHub, so a caller that
   * renders it is rendering state rather than intent.
   */
  setAddisPause: (system: string, paused: boolean) =>
    client.post<AddisPauseResponse>(`/autobuild/pause/${system}`, { paused }),
  /** `/admin/automation/workloads` — Actions routing audit + per-workflow cost (A7Y-3475). */
  getWorkloads: () => client.get<WorkloadsStatus>('/autobuild/workloads'),
  /** `/admin/automation/slots` — live state of every UKKO runner slot (A7Y-3600). */
  getUkkoSlots: () => client.get<UkkoSlotsStatus>('/autobuild/slots'),
};
